import { DishAllergen } from "api-types";

import React from "react";

import { Box, Typography, useTheme } from "@mui/material";

import { useTranslation } from "../../../i18n";
import { AllergenChipOutlined } from "../../Chip/Chip";
import { MappedProduct } from "../ProductDrawer/product.types";

interface OptionalAllergenDetailProps {
  allergen: DishAllergen;
  status: "removable" | "partial" | "not_removable";
  removableIngredientNames?: string[];
  removableProducts?: MappedProduct[];
  onProductClick?: (product: MappedProduct) => void;
  nonRemovableItems?: string[];
}

interface AllergenItemsListProps {
  label: string;
  items: string[];
  color: string;
}

const AllergenItemsList = ({ label, items, color }: AllergenItemsListProps) => (
  <Typography
    color={color}
    variant="labelXSLight"
    textAlign="left"
    sx={{ flexWrap: "wrap", display: "flex", gap: "4px" }}
  >
    <span style={{ fontStyle: "italic" }}>{label}</span>{" "}
    {items.map((text, idx) => (
      <span key={`${label}-${idx}`}>
        {text}
        {idx < items.length - 1 ? ", " : ""}
      </span>
    ))}
  </Typography>
);

const hasItems = (arr?: unknown[]) => Array.isArray(arr) && arr.length > 0;

export const OptionalAllergenDetail = ({
  allergen,
  status,
  removableIngredientNames,
  removableProducts,
  onProductClick,
  nonRemovableItems,
}: OptionalAllergenDetailProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { oscar } = theme.palette;

  const chipLabel =
    status === "removable"
      ? t("dish.itemCanBeRemoved", { name: allergen.allergenName })
      : allergen.allergenName;

  const showRemovable =
    status !== "not_removable" &&
    (hasItems(removableIngredientNames) || hasItems(removableProducts));
  const showNonRemovable =
    status !== "removable" && hasItems(nonRemovableItems);

  const removableItems = [
    ...(removableIngredientNames ?? []).map((name) => ({
      kind: "ingredient" as const,
      key: `ing-${name}`,
      name,
    })),
    ...(removableProducts ?? []).map((product) => ({
      kind: "product" as const,
      key: `prod-${product.id}`,
      product,
    })),
  ];

  return (
    <Box
      key={allergen.igfdTaxAllergenId}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={0.5}
    >
      <AllergenChipOutlined allergenName={chipLabel} />

      {showNonRemovable && (
        <AllergenItemsList
          label={t("allergenDetail.contains")}
          items={nonRemovableItems!}
          color={oscar.light.content.content1}
        />
      )}

      {showRemovable && (
        <Typography
          color={oscar.light.content.content1}
          variant="labelXSLight"
          textAlign="left"
          sx={{ flexWrap: "wrap", display: "flex", gap: "4px" }}
        >
          <span style={{ fontStyle: "italic" }}>
            {t("allergenDetail.askToRemove")}
          </span>{" "}
          {removableItems.map((item, idx) => (
            <span key={item.key}>
              {item.kind === "ingredient" ? (
                <span>{item.name}</span>
              ) : (
                <span
                  onClick={() => onProductClick?.(item.product)}
                  style={{
                    color: theme.palette.oscar.light.content.link,
                    textDecoration: onProductClick ? "underline" : "none",
                    cursor: onProductClick ? "pointer" : "default",
                  }}
                >
                  {item.product.name}
                </span>
              )}
              {idx < removableItems.length - 1
                ? idx === removableItems.length - 2
                  ? t("common.listLastSeparator")
                  : t("common.listSeparator")
                : ""}
            </span>
          ))}
        </Typography>
      )}
    </Box>
  );
};
